import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileDown } from "lucide-react";
import { MonthPicker } from "@/components/DatePicker";

function thisMonth() { return new Date().toISOString().slice(0, 7); }

const STATUS_CLASS = {
  "Present": "status-completed",
  "Working": "status-ongoing",
  "Holiday": "status-ongoing",
  "Weekly Off": "status-pending",
  "Leave": "status-rejected",
  "Half Day": "status-pending",
  "Short Day": "status-cancelled",
  "Absent": "status-cancelled",
  "—": "",
};
const STATUS_SHORT = {
  "Present": "P",
  "Working": "W",
  "Holiday": "H",
  "Weekly Off": "WO",
  "Leave": "L",
  "Half Day": "½",
  "Short Day": "S",
  "Absent": "A",
  "—": "·",
};

export default function MonthlyView({ currentUser }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("all");

  useEffect(() => {
    (async () => {
      const r = await api.get("/users").catch(() => ({ data: [] }));
      setUsers(r.data || []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const params = { month };
      if (userId && userId !== "all") params.user_id = userId;
      const r = await api.get("/attendance/monthly-stats", { params });
      setData(r.data);
    })();
  }, [month, userId]);

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="gc-label mb-1">Month</div>
          <MonthPicker value={month} onChange={(v) => setMonth(v || month)} className="w-44" testId="input-month" />
        </div>
        <div>
          <div className="gc-label mb-1">Employee</div>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="w-64 rounded-sm" data-testid="select-employee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {users.map((u) => (<SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto inline-flex items-center gap-3">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/attendance/payroll-export?month=${month}`}
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-200 hover:border-[#0a2540] text-xs rounded-sm font-medium"
            data-testid="btn-payroll-export"
            title="Download Payroll Register (Excel)"
          >
            <FileDown size={13} /> Payroll Register
          </a>
          <div className="inline-flex items-center gap-3 text-[10px] gc-label">
            <Legend color="status-completed" label="Present (P)" />
            <Legend color="status-ongoing" label="Holiday (H)" />
            <Legend color="status-pending" label="Weekly Off (WO)" />
            <Legend color="status-rejected" label="Leave (L)" />
            <Legend color="status-cancelled" label="Absent (A)" />
          </div>
        </div>
      </div>

      {(data.rows || []).map((r) => (
        <MonthlyRow key={r.user_id} row={r} />
      ))}
      {(data.rows || []).length === 0 && <div className="text-xs text-slate-400 text-center py-10 border border-dashed border-slate-200">No data</div>}
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1 normal-case tracking-normal">
      <span className={`status-pill ${color}`} style={{ minWidth: 18, padding: "0 6px" }}>·</span>
      <span>{label}</span>
    </span>
  );
}

function MonthlyRow({ row }) {
  const s = row.summary;
  return (
    <div className="border border-slate-200" data-testid={`monthly-row-${row.user_id}`}>
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>{row.user_name}</div>
          <div className="text-[11px] text-slate-500 font-mono-data">{row.user_email} · {row.role}</div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          <Stat label="Present" value={s.present} />
          <Stat label="Holidays" value={s.holidays} />
          <Stat label="Weekly Off" value={s.weekly_off} />
          <Stat label="Leaves" value={s.leaves_total} />
          <Stat label="Absent" value={s.absent} accent="red" />
          <Stat label="Paid Days" value={s.paid_days} accent="primary" />
        </div>
      </div>
      <div className="px-3 py-3">
        <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(42px, 1fr))" }}>
          {row.days.map((d) => (
            <div
              key={d.date}
              title={`${d.date} (${d.weekday}) — ${d.status}${d.half_day_period ? ` (${d.half_day_period})` : ""}${d.check_in ? ` · in ${formatTime(d.check_in)}` : ""}${d.check_out ? ` · out ${formatTime(d.check_out)}` : ""}${d.leave_type ? ` · ${d.leave_type}` : ""}${d.holiday_name ? ` · ${d.holiday_name}` : ""}`}
              className={`status-pill ${STATUS_CLASS[d.status] || ""} text-[10px] justify-center`}
              style={{ minWidth: 36, padding: "1px 0", textAlign: "center" }}
              data-testid={`day-${row.user_id}-${d.date}`}
            >
              {new Date(d.date).getDate()}{STATUS_SHORT[d.status] !== "·" ? ` ${STATUS_SHORT[d.status]}` : ""}
            </div>
          ))}
        </div>
        {Object.keys(s.leaves_by_type || {}).length > 0 && (
          <div className="mt-3 text-[11px] text-slate-500">
            <span className="gc-label mr-2">Leave breakup</span>
            {Object.entries(s.leaves_by_type).map(([k, v]) => (
              <span key={k} className="mr-3 font-mono-data">{k}: <span className="text-[#0a2540]">{v}</span></span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color = accent === "red" ? "text-red-700" : accent === "primary" ? "text-[#0a2540]" : "text-slate-800";
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-slate-500 uppercase tracking-wider text-[10px]">{label}</span>
      <span className={`font-mono-data font-semibold ${color}`}>{value}</span>
    </div>
  );
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}
